import { useEffect, useRef } from 'react';

import { applyDetachedSession } from './apply-detached-session';
import {
  clearDetachedHandoffQueryParams,
  clearDetachedSession,
  DetachedSessionPayload,
  loadDetachedSession,
} from './detached-session';
import { isDetachedMode, setDetachedMode } from './embedded-mode';
import { notifyShellDetachReady } from './web-wrapper/web-wrapper-shell';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';

const getTransferId = (): string | null =>
  new URLSearchParams(window.location.search).get('transferId');

export const bootstrapDetachedMode = (): {
  payload: DetachedSessionPayload;
  transferId: string;
} | null => {
  const transferId = getTransferId();
  if (!transferId || !isDetachedMode()) {
    return null;
  }

  setDetachedMode(true);
  const payload = loadDetachedSession(transferId);
  return payload ? { transferId, payload } : null;
};

export const useDetachedModeBoot = (gameLoadSuccess: boolean): void => {
  const consumedTransferIdRef = useRef<string | null>(null);

  useEffect(() => {
    const boot = bootstrapDetachedMode();
    if (!boot) {
      return;
    }

    Logger.logRumAction({
      name: '[GW][DETACHED] Bootstrap Detached Mode',
      useCase: DdEventCategory.Session,
      eventType: DdEventType.Action,
    });

    const { transferId, payload } = boot;
    consumedTransferIdRef.current = transferId;
    applyDetachedSession(payload);
    clearDetachedSession(transferId);
    clearDetachedHandoffQueryParams();
  }, []);

  useEffect(() => {
    if (!isDetachedMode() || !gameLoadSuccess) {
      return;
    }

    const transferId = consumedTransferIdRef.current ?? getTransferId();
    if (transferId) {
      void notifyShellDetachReady(transferId);
      Logger.logRumAction({
        name: '[GW][DETACHED] Notify Shell Detach Ready',
        useCase: DdEventCategory.Session,
        eventType: DdEventType.Action,
      });
      consumedTransferIdRef.current = null;
    }
  }, [gameLoadSuccess]);
};
